import { ImportTypeState } from './transactions/state/import-type.state';
import { CommonModule } from '@angular/common';
import {
  HttpClient,
  HttpClientModule,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, Injector, NgModule } from '@angular/core';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFireAuthModule } from '@angular/fire/compat/auth';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
// import { FullCalendarModule } from '@fullcalendar/angular';
// import dayGridPlugin from '@fullcalendar/daygrid';
// import interactionPlugin from '@fullcalendar/interaction';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { FormlyBootstrapModule } from '@ngx-formly/bootstrap';
import { FormlyModule } from '@ngx-formly/core';
import { NgxsModule, Store } from '@ngxs/store';
import { firstValueFrom } from 'rxjs';
import { RichTextEditorAllModule } from '@syncfusion/ej2-angular-richtexteditor';
import { NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';
import { ToastrModule } from 'ngx-toastr';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { PanelModule } from 'primeng/panel';
import { RadioButtonModule } from 'primeng/radiobutton';
// PrimeNG Module
import { StepsModule } from 'primeng/steps';
import { TableModule } from 'primeng/table';
import { environment } from './../environments/environment';
import { AppComponent } from './app.component';
import { routing } from './app.routing';
import { AuthGuard } from './core/auth.guard';
import { FormBuilderTypeSafe } from './core/form-type-safe-helper';
import { AddHeaderInterceptor } from './core/http-interceptor';
import { ReadMeComponent } from './read-me/read-me.component';
import { SaleCampaignState } from './sale-campaign/state/sale-campaign.state';
import { GoogleAnalyticsService } from './services/google-analytics/google-analytics.service';
import { LookupService } from './services/lookup/lookup.service';
import { NoteService } from './services/notes/note.service';
import { OrganizationService } from './services/organization/organization.service';
import { ReportService } from './services/reports/report.service';
import { TransactionService } from './services/transactions/transaction.service';
import { UserResolver } from './services/user/user.resolver';
import { UserService } from './services/user/user.service';
import { AutoCompleteComponent } from './shared/components/auto-complete/auto-complete.component';
import { App } from './state/app.actions';
import { ApplicationState } from './state/app.state';
import { MemberState } from './state/member.state';
import { UserState } from './users/user.state';
import { ServiceLocator } from './services/locator.service';
import { TicketState } from './ticket-list/state/ticket.state';
import { ApplePayComponent } from './apple-pay/apple-pay.component';
import { NgxTranslateModule } from './translate/translate.module';
import { SplitTransactionState } from './transactions/state/split-transaction/split-transaction.state';
import { PriceState } from './transactions/state/product/product.state';
import { DuesyModule } from './duesy/duesy.module';

// FullCalendarModule.registerPlugins([dayGridPlugin, interactionPlugin]);

export class ChunkErrorHandler implements ErrorHandler {
  handleError(error: any): void {
    const chunkFailedPattern = /Loading chunk \d+ failed/;
    if (chunkFailedPattern.test(error?.message)) {
      // Stale chunk after a new deployment — reload once to pick up fresh bundles.
      window.location.reload();
      return;
    }
    console.error(error);
  }
}

export function initApp(store: Store) {
  // Blocks router navigation until the existing token (if any) has been
  // validated against the backend and session state (user, org, roles) is
  // fully restored — or confirmed absent/invalid. Without this, AuthGuard
  // would evaluate against empty NGXS state on every hard refresh.
  return () =>
    firstValueFrom(store.dispatch(new App.Actions.InitializeSession())).catch(
      () => undefined
    );
}

@NgModule({
  declarations: [
    AppComponent,
    AutoCompleteComponent,
    ReadMeComponent,
    ApplePayComponent,
  ],
  imports: [
    AngularFireModule.initializeApp(environment.firebase),
    // AngularFirestoreModule, // imports fi rebase/firestore, only needed for database features
    AngularFireAuthModule, // imports firebase/auth, only needed for auth features
    BrowserModule,
    CommonModule,
    routing,
    NgbModule,
    BrowserAnimationsModule,
    ToastrModule.forRoot(),
    RichTextEditorAllModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    AccordionModule,
    PanelModule,
    ButtonModule,
    RadioButtonModule,
    StepsModule,
    TableModule,
    DialogModule,
    ConfirmDialogModule,
    NgxTranslateModule,
    FormlyModule.forRoot(),
    FormlyBootstrapModule,
    //State Management
    NgxsModule.forRoot(
      [
        MemberState,
        UserState,
        ApplicationState,
        SaleCampaignState,
        ImportTypeState,
        TicketState,
        SplitTransactionState,
        PriceState,
      ],
      {
        developmentMode: !environment.production,
      }
    ),
    NgMultiSelectDropDownModule.forRoot(),
    DuesyModule,
  ],
  providers: [
    UserService,
    AuthGuard,
    UserResolver,
    OrganizationService,
    NoteService,
    TransactionService,
    ReportService,
    LookupService,
    HttpClient,
    {
      provide: APP_INITIALIZER,
      useFactory: initApp,
      multi: true,
      deps: [Store],
    },
    FormBuilderTypeSafe,
    GoogleAnalyticsService,
    { provide: HTTP_INTERCEPTORS, useClass: AddHeaderInterceptor, multi: true },
    { provide: ErrorHandler, useClass: ChunkErrorHandler },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(private injector: Injector) {
    // Create global Service Injector.
    ServiceLocator.injector = this.injector;
  }
}
